/**
 * QuotationsContainer Component
 * Main container with sub-tabs for Purchase and Sales Quotations
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePurchaseQuotations, useSalesQuotations, useDeletePurchaseQuotation, useDeleteSalesQuotation } from '@/hooks/useQuotations';
import { QuotationListView } from './QuotationListView';
import { QuotationDialog } from './QuotationDialog';
import type { Quotation, QuotationFilter } from '@/types/quotations';

type SubTab = 'purchase' | 'sales';

export const QuotationsContainer: React.FC = () => {
  const { t } = useTranslation('psi');
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('purchase');
  const [filter, setFilter] = useState<QuotationFilter>({ limit: 20, offset: 0 });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState<Quotation | null>(null);
  const [deleteConfirmQuotation, setDeleteConfirmQuotation] = useState<Quotation | null>(null);

  // Data hooks
  const purchaseQuery = usePurchaseQuotations(activeSubTab === 'purchase' ? filter : undefined);
  const salesQuery = useSalesQuotations(activeSubTab === 'sales' ? filter : undefined);
  const deletePurchaseMutation = useDeletePurchaseQuotation();
  const deleteSalesMutation = useDeleteSalesQuotation();

  const currentQuery = activeSubTab === 'purchase' ? purchaseQuery : salesQuery;
  const quotations = currentQuery.data?.quotations || [];
  const total = currentQuery.data?.total || 0;

  const handleAddQuotation = () => {
    setEditingQuotation(null);
    setIsDialogOpen(true);
  };

  const handleEditQuotation = (quotation: Quotation) => {
    setEditingQuotation(quotation);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (quotation: Quotation) => {
    setDeleteConfirmQuotation(quotation);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmQuotation) return;

    try {
      if (activeSubTab === 'purchase') {
        await deletePurchaseMutation.mutateAsync(deleteConfirmQuotation.quotation_id);
      } else {
        await deleteSalesMutation.mutateAsync(deleteConfirmQuotation.quotation_id);
      }
      setDeleteConfirmQuotation(null);
    } catch (error) {
      console.error('Failed to delete quotation:', error);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingQuotation(null);
  };

  const handleDialogSuccess = () => {
    setIsDialogOpen(false);
    setEditingQuotation(null);
    currentQuery.refetch();
  };

  // Pagination
  const handlePageChange = (newOffset: number) => {
    setFilter((prev) => ({ ...prev, offset: newOffset }));
  };

  const pageSize = filter.limit || 20;
  const currentPage = Math.floor((filter.offset || 0) / pageSize) + 1;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      {/* Header with sub-tabs and actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Sub-tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveSubTab('purchase')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'purchase'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('quotations.purchase')}
          </button>
          <button
            onClick={() => setActiveSubTab('sales')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'sales'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t('quotations.sales')}
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleAddQuotation}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                     transition-colors text-sm font-medium flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('quotations.addQuotation')}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={filter.valid_only || false}
            onChange={(e) => setFilter((prev) => ({ ...prev, valid_only: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {t('quotations.filters.validOnly')}
          </span>
        </label>
      </div>

      {/* List view */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow">
        <QuotationListView
          quotations={quotations}
          type={activeSubTab}
          isLoading={currentQuery.isLoading}
          onEdit={handleEditQuotation}
          onDelete={handleDeleteClick}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('common.pagination.showing', {
              from: (filter.offset || 0) + 1,
              to: Math.min((filter.offset || 0) + pageSize, total),
              total,
            })}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange((filter.offset || 0) - pageSize)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded
                       disabled:opacity-50 disabled:cursor-not-allowed
                       hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {t('common.previous')}
            </button>
            <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => handlePageChange((filter.offset || 0) + pageSize)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded
                       disabled:opacity-50 disabled:cursor-not-allowed
                       hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      {isDialogOpen && (
        <QuotationDialog
          type={activeSubTab}
          quotation={editingQuotation}
          onClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmQuotation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {t('quotations.deleteConfirm.title')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {t('quotations.deleteConfirm.message', { id: deleteConfirmQuotation.quotation_id })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmQuotation(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletePurchaseMutation.isLoading || deleteSalesMutation.isLoading}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationsContainer;
